import * as z from 'zod';
import { FIRST_PAGE } from '@noor/config';

/** The slots list pages by 10 (cpanel `limit || 10`). */
export const SLOT_DEFAULT_PAGE_SIZE = 10;

/** Rows-per-page choices shown in the legacy bonus list. */
export const SLOT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

/** The reserves dialog pages by 20 (cpanel `defaultOffsetSize`). */
export const SLOT_RESERVES_DEFAULT_PAGE_SIZE = 20;

export const slotModalSchema = z.enum(['create', 'edit']);
export type SlotModal = z.infer<typeof slotModalSchema>;

/**
 * Coerced + caught (never strict): the router JSON-parses search params, so a
 * bare numeric link (`?slotId=7`) arrives as a number, and a stale value must
 * fall back to unset instead of crashing route validation.
 */
const optionalString = z.coerce.string().optional().catch(undefined);
const page = z.coerce.number().int().positive().catch(FIRST_PAGE).default(FIRST_PAGE);
const pageSize = (fallback: number) =>
  z.coerce.number().int().positive().catch(fallback).default(fallback);

/**
 * URL search-param schema of `/slot`. Keeps cpanel's query keys (`modalType`,
 * `slotId`, `searchReserves`, `reserve_start_date`, `reserve_end_date`) so
 * deep links to an open drawer survive; paging moved from `limit`/`offset` to
 * `page`/`pageSize` (and `reservesPage`/`reservesPageSize` for the reserves).
 */
export const slotListSearchSchema = z.object({
  page,
  pageSize: pageSize(SLOT_DEFAULT_PAGE_SIZE),
  /** Active-period date window used by the bonus-list filter. */
  start_date: optionalString,
  end_date: optionalString,
  /** Which dialog is open; `edit` and `reserves` need `slotId`. */
  modalType: slotModalSchema.optional().catch(undefined),
  slotId: optionalString,
  searchReserves: optionalString,
  /** Reserves day window (`YYYY-MM-DD`); unset means today. */
  reserve_start_date: optionalString,
  reserve_end_date: optionalString,
  reservesPage: page,
  reservesPageSize: pageSize(SLOT_RESERVES_DEFAULT_PAGE_SIZE),
});
export type SlotListSearch = z.infer<typeof slotListSearchSchema>;
